'use client';

import React from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { PulseButton, BrandIcon } from 'src/components';
import { truncateString } from 'src/utils/truncateString/truncateString';

interface PushToCRMV2Props {
  id?: string;
  hasLinks?: boolean;
  integration?: string;
  disabled?: boolean;
  isLoading?: boolean;
  label: string;
  variant?: 'compact' | 'regular';
  onSubmit: (e: React.MouseEvent<HTMLButtonElement>) => void;
  testIdPrefix?: string;
  isOpen?: boolean;
}

/**
 * PushToCRM V2 button component using PulseButton and Tailwind styling
 * Replaces MUI-based PushToCRM button with modern design system
 */
const PushToCRMV2: React.FC<PushToCRMV2Props> = ({
  hasLinks = false,
  isLoading = false,
  integration = 'salesforce',
  disabled = false,
  label = 'Push to CRM',
  variant = 'regular',
  onSubmit,
  testIdPrefix = '',
  isOpen = false,
}): React.ReactElement => {
  const testId = testIdPrefix
    ? `${testIdPrefix}-push-to-crm-button`
    : 'push-to-crm-button';

  // Compact variant - icon only button
  if (variant === 'compact') {
    return (
      <div className="group relative inline-block" title={label}>
        <PulseButton
          color="primary"
          variant={hasLinks ? 'outline' : 'solid'}
          size="sm"
          onClick={onSubmit}
          disabled={disabled || isLoading}
          className="rounded-full min-w-0"
          data-testid={testId}
        >
          {isLoading ? (
            <Icon icon={faSpinner} className="animate-spin" />
          ) : (
            <div className="flex items-center gap-1">
              {hasLinks && (
                <Icon
                  icon={faCaretDown}
                  className={`w-3 h-3 transition-all duration-300 ${
                    isOpen ? '-rotate-90' : ''
                  }`}
                />
              )}
            </div>
          )}
        </PulseButton>
      </div>
    );
  }

  // Regular variant - full button with text
  return (
    <PulseButton
      color="primary"
      variant="solid"
      size="sm"
      onClick={onSubmit}
      disabled={disabled || isLoading}
      className="rounded-full max-w-full overflow-hidden"
      data-testid={testId}
    >
      <div className="flex items-center gap-2 min-w-0">
        {isLoading ? (
          <Icon icon={faSpinner} className="animate-spin" />
        ) : (
          <BrandIcon
            brand={integration?.toLowerCase() || 'salesforce'}
            width="1.1rem"
            height="1rem"
            invert={true}
          />
        )}
        <span className="truncate">{truncateString(label, 12)}</span>
      </div>
      {hasLinks && !isLoading && (
        <Icon
          icon={faCaretDown}
          className={`w-3 h-3 transition-all duration-300 ${
            isOpen ? '-rotate-90' : ''
          }`}
        />
      )}
    </PulseButton>
  );
};

export default PushToCRMV2;
